import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Grid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Slot as SlotComponent } from './Slot';
import { DragLayer } from '../dnd/DragLayer';
import { useDrop } from 'react-dnd';
import { ItemTypes } from '../dnd/types';
import { Content } from '../shared/types';
import { ContentComponent } from '../shared/ContentComponent';
import ContentBox from '../dnd/ContentDrag';
import { shuffleArray } from '../../../../../../helpers/shuffle';
import { dirFromDndPos, flexDirFromDndPos } from '../shared/utils';
import { useIsMobile } from '../../../../../../hooks/isMobile';
import { useTranslation } from 'react-i18next';

export type Slot = {
  key: string;
  index: number;
  answer: string;
  caseSensitive?: string;
};

type Association = {
  left: Content;
  right: Content;
};

interface AssociationsProps extends BoxProps {
  associations: Association[];
  debug?: boolean;
  showCorrect?: boolean;
  dndPosition: 'left' | 'top' | 'right' | 'bottom' | string;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: State;
  isViewOnly?: boolean;
  onCanShowCorrectChange?: (canShow: boolean) => void;
}

type State = {
  answers: Record<
    string,
    { value: string; correct: boolean; type: 'text' | 'image' | 'audio' }
  >;
  slot: Record<string, string>;
};

const Associations: React.FC<AssociationsProps> = ({
  associations,
  debug,
  showCorrect,
  dndPosition = 'right',
  submit,
  reset,
  initialState,
  isViewOnly,
  onCanShowCorrectChange,
  ...rest
}) => {
  const [state, setState] = useState<State>({ answers: {}, slot: {} });
  const [showCorrectAnswers, setShowCorrectAnswers] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState<string>('');
  const poolRef = useRef<HTMLDivElement>(null);
  // const indexMap = useMemo(() => {
  //   const map: Map<number, Slot> = new Map();

  //   associations.forEach((s, index) => {
  //     map.set(index, s);
  //   });

  //   return map;
  // }, [associations]);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const [cAssociations, shuffledAssocs, hasImg] = useMemo(() => {
    let hasImg = false;
    const assocs = associations.map((a, index) => {
      if (a.left.type === 'image' || a.right.type === 'image') {
        hasImg = true;
      }
      return {
        ...a,
        slot: {
          index,
          type: a.right.type,
          answer: a.right.src,
          key: `a${index + 1}`,
        },
      };
    });

    const shuffledAssocs = [...assocs];

    shuffleArray(shuffledAssocs);

    return [assocs, shuffledAssocs, hasImg];
  }, []);

  const [{ hovering }, drop] = useDrop({
    accept: ItemTypes.ContentBox,
    drop: (item: { id: string }, monitor) => {
      if (showResults) return;
      if (monitor.isOver({ shallow: true })) {
        let newState = { ...state };
        const answerKey = newState.slot[item.id]!;
        delete newState.answers[answerKey];
        delete newState.slot[item.id];
        setState(newState);
      }
    },
    collect: (monitor) => ({
      hovering: !showResults && monitor.isOver(),
    }),
  });

  const updateValueFn = (
    toKey: string,
    newValue: string,
    fromKey: string,
    type: 'text' | 'image' | 'audio',
  ) => {
    let newState = { ...state };
    let oldAnswerKey = newState.slot[fromKey];
    let oldSlot = Object.keys(newState.slot).find(
      (s) => newState.slot[s] === toKey,
    );
    if (oldAnswerKey) {
      if (newState.answers[toKey] != null) {
        newState.answers[oldAnswerKey] = { ...newState.answers[toKey]! };
      } else {
        delete newState.answers[oldAnswerKey];
      }
    }
    if (oldSlot) {
      if (newState.slot[oldSlot] != null) {
        newState.slot[oldSlot] = oldAnswerKey!;
      }
    }
    newState.slot[fromKey] = toKey;
    newState.answers[toKey]! = {
      value: newValue,
      correct: false,
      type,
    };
    Object.keys(newState.answers).forEach((key) => {
      const assoc = cAssociations.find((s) => s.slot.key === key);
      if (assoc) {
        newState.answers[key]!.correct =
          newState.answers[key]!.value === assoc.slot.answer;
      }
    });
    setState(newState);
  };

  const announce = (msg: string) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(msg));
  };

  const handlePickItem = (id: string) => {
    if (showResults) return;
    if (selectedItemId === id) {
      setSelectedItemId(null);
      announce('Selection cancelled.');
    } else {
      setSelectedItemId(id);
      const item = shuffledAssocs.find(s => s.slot.key === id);
      const label = item?.right.type === 'text' ? item.slot.answer : 'item';
      announce(`${label} selected. Tab to a slot and press Space or Enter to place it.`);
    }
  };

  const handleDropOnSlot = (slotId: string) => {
    if (!selectedItemId || showResults) return;
    const item = shuffledAssocs.find(s => s.slot.key === selectedItemId);
    if (!item) return;

    // A-3: detect if slot already has an item so we can announce the swap
    const displacedItemKey = Object.keys(state.slot).find(k => state.slot[k] === slotId);
    const displacedAssoc = displacedItemKey ? shuffledAssocs.find(s => s.slot.key === displacedItemKey) : null;
    const displacedLabel = displacedAssoc?.right.type === 'text' ? displacedAssoc.slot.answer : null;

    updateValueFn(slotId, item.slot.answer, item.slot.key, item.right.type);
    const label = item.right.type === 'text' ? item.slot.answer : 'item';
    const slotAssoc = cAssociations.find(c => c.slot.key === slotId);
    const slotLabel = slotAssoc?.left.type === 'text' ? slotAssoc.left.src : 'slot';
    const baseMsg = `${label} placed in ${slotLabel}.`;
    announce(displacedLabel ? `${baseMsg} ${displacedLabel} returned to the pool.` : baseMsg);
    setSelectedItemId(null);

    // A-2: restore focus to the first available pool item after drop
    requestAnimationFrame(() => {
      const firstItem = poolRef.current?.querySelector<HTMLElement>('[tabindex="0"]');
      firstItem?.focus();
    });
  };

  const removeFromSlot = (slotKey: string) => {
    const itemId = Object.keys(state.slot).find(k => state.slot[k] === slotKey);
    if (!itemId) return;
    const newState = {
      answers: { ...state.answers },
      slot: { ...state.slot },
    };
    delete newState.answers[slotKey];
    delete newState.slot[itemId];
    setState(newState);
    setSelectedItemId(null);
  };

  const setInitialState = () => {
    setState({ answers: {}, slot: {} });
    setSelectedItemId(null);
  };

  useEffect(() => {
    if (initialState) {
      setState(initialState);
      setShowResults(true);
      let correct = 0;

      Object.keys(initialState.answers).forEach((key) => {
        if (initialState.answers[key]!.correct) {
          correct += 1;
        }
      });

      setShownCorrect(correct);
    } else setInitialState();
  }, [associations, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state.answers).forEach((key) => {
      if (state.answers[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, associations.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    setSelectedItemId(null);
    if (reset != null) reset();
  };

  const isAllAnswered =
    Object.keys(state.answers).length === associations.length;

  useEffect(() => {
    onCanShowCorrectChange?.(!showResults && !isAllAnswered);
  }, [showResults, isAllAnswered, onCanShowCorrectChange]);

  const ret = (
    <VStack
      w="full"
      spacing={0}
      gap={4}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && selectedItemId) {
          setSelectedItemId(null);
          announce('Selection cancelled.');
        }
      }}
    >
      {/* aria-live region for keyboard operation announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        position="absolute"
        w="1px"
        h="1px"
        overflow="hidden"
        style={{ clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
      >
        {announcement}
      </Box>
      <Stack
        w="full"
        ref={drop}
        alignItems="center"
        gap={4}
        display="flex"
        flexDir={flexDirFromDndPos(dndPosition)}
        {...rest}
      >
        <VStack flexGrow={4} justify="space-evenly" w="full" display='flex' flexDirection='column' alignItems='flex-start'>
          {cAssociations.map((c) => {
            const s2 = Object.keys(state.slot).find(
              (s) => state.slot[s] === c.slot.key,
            );
            const hasSlotContent =
              !!state.answers[c.slot.key] ||
              showCorrect ||
              showCorrectAnswers;
            return (
              <Grid
                key={c.slot.key}
                w="full"
                gap={2}
                gridTemplateColumns={'1fr auto 1fr'}
                alignContent="center"
                minH={hasImg ? '100px' : undefined}
                padding='10px'
              >
                {ContentComponent({
                  ...c.left,
                  textAlign: 'left',
                  maxW: c.left.type === 'image' ? '100px' : undefined,
                  maxH: c.left.type === 'image' ? '100px' : undefined,
                  fontSize: isMobile ? '12px' : '16px',
                  minWidth: '80px',
                })}
                <Text as="div" alignSelf="center" color="gray.500"  margin='0 30px'>
                  <b>&lt;=&gt;</b>
                </Text>
                <SlotComponent
                  id={c.slot.key}
                  key={c.slot.key}
                  slot={s2}
                  value={state.answers[c.slot.key]?.value}
                  type={state.answers[c.slot.key]?.type}
                  placeholder={
                    showCorrect || showCorrectAnswers ? c.right : undefined
                  }
                  updateValue={updateValueFn}
                  justifySelf={hasSlotContent ? 'stretch' : 'end'}
                  alignSelf="center"
                  minWidth={hasSlotContent ? '100%' : '80px'}
                  isDisabled={showResults}
                  fontSize={isMobile ? '12px' : '16px'}
                  onKeyboardDrop={() => handleDropOnSlot(c.slot.key)}
                  hasKeyboardSelection={selectedItemId !== null}
                  onRemove={() => removeFromSlot(c.slot.key)}
                />
              </Grid>
            );
          })}
        </VStack>
        <Stack
          spacing={0}
          gap={2}
          flexGrow={1}
          ml={4}
          background={hovering ? 'rgba(208,208,208,0.45)' : undefined}
          borderRadius="8px"
          direction={dirFromDndPos(dndPosition)}
          w="full"
          minH={dirFromDndPos(dndPosition) === 'row' ? '40px' : undefined}
          minW={dirFromDndPos(dndPosition) === 'column' ? '120px' : undefined}
          align="center"
          alignSelf="stretch"
          p={2}
        >
          <Grid
            ref={poolRef}
            templateColumns="repeat(auto-fill, minmax(180px, 1fr))"
            gap={3}
            w="full"
            justifyItems="center"
            alignItems="center"
          >
            {shuffledAssocs
              .filter(
                (s) =>
                  showCorrectAnswers || state.slot[s.slot.key] === undefined,
              )
              .map((s) => (
                <ContentBox
                  id={s.slot.key}
                  key={s.slot.key}
                  type={s.right.type}
                  src={s.slot.answer}
                  boxShadow="sm"
                  fontSize={isMobile ? '12px' : '16px'}
                  allowWrap
                  maxW={{ base: '100%', md: '300px' }}
                  onKeyboardSelect={() => handlePickItem(s.slot.key)}
                  isKeyboardSelected={selectedItemId === s.slot.key}
                  isKeyboardDisabled={showResults}
                />
              ))}
          </Grid>
        </Stack>
      </Stack>
      {showResults && (
        <VStack align="center" gap={2} w="full" >
          <hr style={{ width: '100%', border: '1px solid #000' }} />
          <Text>{t('plugin.associations.answersSubmitted')}</Text>
          <Text fontWeight="medium">
            {t('plugin.associations.result', {
              correct: shownCorrect,
              total: associations.length,
            })}
          </Text>
          {shownCorrect < associations.length && (
            <VStack align="center" gap={2}>
              <Text>{t('plugin.associations.wantToTryAgain')}</Text>
              <Button
                size="sm"
                colorScheme="stemiBlue"
                variant="outline"
                onClick={_reset}
              >
                {t('plugin.interactiveQuiz.tryAgain')}
              </Button>
            </VStack>
          )}
        </VStack>
      )}
      {!isViewOnly && !showResults && (
        <Button
          colorScheme="blue"
          onClick={_submit}
          isDisabled={!isAllAnswered}
        >
          {t('general.submit')}
        </Button>
      )}
    </VStack>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <DragLayer />
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <DragLayer />
      {ret}
    </>
  );
};

export default Associations;
